import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useGetCustomers } from '~/services/customer';

interface CustomAttribute {
  id: string;
  name: string;
  value: string;
  type: string;
  updated_at?: string;
}

interface Customer {
  _id: string;
  person_id: string;
  name: string;
  email?: string;
  phone?: string;
  created_at: string;
  last_activity?: string;
  custom_attributes?: CustomAttribute[];
}

interface CustomerSearchProps {
  agentId: string;
  onSearchResults: (customers: Customer[], isSearchActive: boolean) => void;
  onClearResults: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

export const CustomerSearch: React.FC<CustomerSearchProps> = ({
  agentId,
  onSearchResults,
  onClearResults,
  onLoadingChange,
}) => {
  const [t] = useTranslation('customer');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allCustomers, setAllCustomers] = useState<Customer[]>([]);
  
  const [getCustomers, { data, loading: queryLoading, error }] = useGetCustomers();

  // Load all customers on component mount
  useEffect(() => {
    const loadAllCustomers = async () => {
      setLoading(true);
      if (onLoadingChange) {
        onLoadingChange(true);
      }
      
      try {
        await getCustomers({
          variables: {
            data: {
              agent_id: agentId,
            },
          },
        });
      } catch (error) {
        console.error('Error loading customers:', error);
        onSearchResults([], false);
        setIsSearchActive(false);
      } finally {
        setLoading(false);
        if (onLoadingChange) {
          onLoadingChange(false);
        }
      }
    };

    loadAllCustomers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]); // Depend on agentId instead of empty array

  // Handle GraphQL response
  useEffect(() => {
    if (data?.get_customers) {
      const response = data.get_customers;
      if (!response.hasError && response.data?.customers) {
        const customers = response.data.customers.map((customer: any) => ({
          _id: customer.person_id,
          person_id: customer.person_id,
          name: customer.profile_name || `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Unknown',
          email: customer.personChannels?.find((ch: any) => ch.channel_type === 'email')?.channel_id || '',
          phone: customer.personChannels?.find((ch: any) => ch.channel_type === 'whatsapp')?.channel_id || '',
          created_at: customer.created_at,
          last_activity: customer.updated_at,
          custom_attributes: (customer.profileValues || []).map((pv: any) => ({
            id: pv._id,
            name: pv.profile_attribute_name,
            value: Array.isArray(pv.value) ? pv.value.join(', ') : pv.value,
            type: Array.isArray(pv.value) ? 'array' : 'string',
            updated_at: pv.updated_at,
          })),
        }));
        
        setAllCustomers(customers);
        onSearchResults(customers, true);
        setIsSearchActive(true);
      } else {
        console.error('Error in response:', response.message);
        onSearchResults([], false);
        setIsSearchActive(false);
      }
    }
  }, [data, onSearchResults]);

  const handleSearch = async () => {
    if (!allCustomers.length) return;

    setLoading(true);
    if (onLoadingChange) {
      onLoadingChange(true);
    }
    
    try {
      // Simulate API delay for UX consistency
      await new Promise(resolve => setTimeout(resolve, 300));
      
      let filteredCustomers;
      
      if (!searchTerm.trim()) {
        // If no search term, show all customers
        filteredCustomers = allCustomers;
      } else {
        // Filter customers based on search term
        filteredCustomers = allCustomers.filter(customer =>
          customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customer.phone?.includes(searchTerm)
        );
      }

      onSearchResults(filteredCustomers, true);
      setIsSearchActive(true);
    } catch (error) {
      console.error('Customer search error:', error);
      onSearchResults([], false);
      setIsSearchActive(false);
    } finally {
      setLoading(false);
      if (onLoadingChange) {
        onLoadingChange(false);
      }
    }
  };

  const handleClearSearch = async () => {
    setLoading(true);
    if (onLoadingChange) {
      onLoadingChange(true);
    }
    
    try {
      // Simulate API delay for UX consistency
      await new Promise(resolve => setTimeout(resolve, 200));
      
      // Show all customers again
      onSearchResults(allCustomers, true);
      setIsSearchActive(true);
    } catch (error) {
      console.error('Error loading customers:', error);
      onSearchResults([], false);
      setIsSearchActive(false);
    } finally {
      setLoading(false);
      if (onLoadingChange) {
        onLoadingChange(false);
      }
    }
  };

  return (
    <div className="mb-4">
      {/* Search Input and Button */}
      <div className="mb-4 flex items-center gap-2">
        <div className="join flex-1">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            placeholder={t('search_customers_placeholder', 'Search customers...')}
            className="input input-bordered join-item w-full"
          />
          <button
            onClick={handleSearch}
            disabled={loading}
            className="btn btn-primary join-item"
          >
            {loading ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                {t('searching', 'Searching...')}
              </>
            ) : (
              <>
                <MagnifyingGlassIcon className="h-4 w-4" />
                {t('search', 'Search')}
              </>
            )}
          </button>
        </div>
        {searchTerm && (
          <button
            onClick={() => {
              setSearchTerm('');
              handleClearSearch();
            }}
            className="btn btn-ghost btn-square"
            title="Clear search"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export type { Customer, CustomAttribute };
